import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Crown,
  Database,
  FileText,
  Layers3,
  Network,
  RefreshCw,
  Settings2,
  Shield,
  Sparkles,
  Stethoscope,
  Users,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";
import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";
import { VipAxiosInstance } from "../../Network/VipAxiosInstance";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";
import { createApiClient } from "../../Network/ApiClient";
import { useAuth } from "Pages/PrivateRoute/AuthContext";

const featuresApi = createApiClient("features");
const actionsApi = createApiClient("");

type LoadState = "loading" | "ok" | "error";

interface EntityStat {
  key: string;
  label: string;
  total: number;
  active: number;
  inactive: number;
  route: string;
  status: LoadState;
  error?: string;
}

interface AuditAction {
  id?: number;
  name?: string;
  type?: string;
  username?: string;
  timestamp?: string;
  entry_name?: string;
  info?: any;
}

interface ModulePage {
  label: string;
  feature?: string;
}

interface ModuleGroup {
  label: string;
  pages: ModulePage[];
}

const ENTITY_DEFINITIONS = [
  {
    key: "patients",
    label: "Patients",
    route: "/PatientRelation",
    client: PatientRelationAxiosInstance,
    endpoint: "getpatient",
  },
  {
    key: "providers",
    label: "Providers",
    route: "/provider",
    client: providerAxiosInstance,
    endpoint: "getProvider",
  },
  {
    key: "doctors",
    label: "Doctors",
    route: "/doctor",
    client: DoctorAxiosInstance,
    endpoint: "getDoctor",
  },
  {
    key: "networks",
    label: "Networks",
    route: "/NewNetwork",
    client: NewNetworkAxiosInstance,
    endpoint: "getNetwork",
  },
  {
    key: "policies",
    label: "Policies",
    route: "/Policy",
    client: PolicyAxiosInstance,
    endpoint: "getpolicy",
  },
  {
    key: "insurancePlans",
    label: "Insurance Plans",
    route: "/Insurance",
    client: InsuranceAxiosInstance,
    endpoint: "getInsurancePlan",
  },
  {
    key: "specialties",
    label: "Specialties",
    route: "/specialty",
    client: SpecialtyAxiosInstance,
    endpoint: "getSpecialty",
  },
  {
    key: "vipLists",
    label: "VIP Lists",
    route: "/vip",
    client: VipAxiosInstance,
    endpoint: "getVip",
  },
] as const;

const ENTITY_VISUALS: Record<
  string,
  { icon: React.ElementType; accent: string; soft: string }
> = {
  patients: { icon: Users, accent: "#2563eb", soft: "#dbeafe" },
  providers: { icon: Building2, accent: "#7c3aed", soft: "#ede9fe" },
  doctors: { icon: Stethoscope, accent: "#0891b2", soft: "#cffafe" },
  networks: { icon: Network, accent: "#db2777", soft: "#fce7f3" },
  policies: { icon: Shield, accent: "#ea580c", soft: "#ffedd5" },
  insurancePlans: { icon: FileText, accent: "#059669", soft: "#d1fae5" },
  specialties: { icon: Layers3, accent: "#4f46e5", soft: "#e0e7ff" },
  vipLists: { icon: Crown, accent: "#ca8a04", soft: "#fef9c3" },
};

const MODULE_GROUPS: ModuleGroup[] = [
  {
    label: "ICD Configuration",
    pages: [
      { label: "Medical ICD Groups" },
      { label: "Custom ICD Groups" },
      { label: "ICD Age and Gender", feature: "RELATION_AGE_GENDER" },
      { label: "Not Allowed Principal Diagnosis", feature: "PRINCIPAL_DIAGNOSED" },
      { label: "ICD Not Covered", feature: "ICD_NOT_COVERED" },
      { label: "ICD Must Come", feature: "ICD_ALLOWED" },
      { label: "ICD Not Allowed", feature: "ICD_NOT_ALLOWED" },
    ],
  },
  {
    label: "Activity Configuration",
    pages: [
      { label: "Activity Medical Group" },
      { label: "Activity Custom Group" },
      { label: "Activity Not Covered", feature: "ACTIVITY_NOT_COVERED" },
      { label: "Activities Together", feature: "ACTIVITIES_TOGETHER" },
      { label: "Activities Not Allowed Together", feature: "ACTIVITIES_NOT_ALLOWED_TOGETHER" },
      { label: "Activity Frequency", feature: "FREQUENCY_CONTROL" },
      { label: "Activity Must Come ICDs", feature: "ACTIVITY_MUST_COME_WITH_ICD" },
      { label: "Activity Not Allowed with ICD", feature: "ACTIVITY_NOT_ALLOWED_WITH_ICD" },
      { label: "Activity Bundle", feature: "ACTIVITY_BUNDLE" },
      { label: "Activity Age and Gender", feature: "ACTIVITY_AGE_GENDER" },
      { label: "Activity Pre-Approvals", feature: "ACTIVITY_PRE_APPROVAL" },
    ],
  },
  {
    label: "Benefit Configuration",
    pages: [
      { label: "Benefit Group" },
      { label: "Benefit", feature: "BENEFIT" },
    ],
  },
  {
    label: "Validation Configuration",
    pages: [
      { label: "Claim Validation" },
      { label: "Bulk Claim Validation" },
      { label: "Validation History" },
    ],
  },
  {
    label: "Configure",
    pages: [
      { label: "Provider", feature: "CONFIG_LEVEL" },
      { label: "Network", feature: "CONFIG_LEVEL" },
      { label: "Policy", feature: "CONFIG_LEVEL" },
      { label: "Patient", feature: "CONFIG_LEVEL" },
      { label: "Insurance Plan", feature: "CONFIG_LEVEL" },
      { label: "Doctor", feature: "CONFIG_LEVEL" },
      { label: "Specialty", feature: "CONFIG_LEVEL" },
      { label: "VIP", feature: "CONFIG_LEVEL" },
    ],
  },
];

const getLocalDateString = (date: Date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const extractRows = (payload: any): any[] => {
  const root = payload?.data ?? payload;
  const result = root?.result;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.rows)) return root.rows;
  if (Array.isArray(root)) return root;

  for (const key of [
    "patients",
    "providers",
    "doctors",
    "networks",
    "policies",
    "insurancePlans",
    "plans",
    "specialties",
    "vips",
    "vipLists",
  ]) {
    if (Array.isArray(root?.[key])) return root[key];
    if (Array.isArray(result?.[key])) return result[key];
  }

  return [];
};

const extractTotalCount = (payload: any): number => {
  const root = payload?.data ?? payload;
  const result = root?.result;

  const candidates = [
    root?.pagination?.totalCount,
    root?.meta?.totalCount,
    root?.totalCount,
    root?.count,
    result && !Array.isArray(result) ? result?.totalCount : undefined,
    result && !Array.isArray(result) ? result?.count : undefined,
  ];

  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n >= 0) return n;
  }

  return extractRows(root).length;
};

const fetchFilteredCount = async (
  client: any,
  endpoint: string,
  active: boolean
): Promise<number> => {
  const response = await client.get(endpoint, {
    params: {
      page: 1,
      limit: 10,
      active: active ? "true" : "false",
      is_deleted: "false",
    },
  });

  return extractTotalCount(response.data);
};

const actionTypeBucket = (type?: string, name?: string) => {
  const raw = `${type ?? ""} ${name ?? ""}`.toLowerCase();
  if (raw.includes("delete")) return "Delete";
  if (raw.includes("restore")) return "Restore";
  if (raw.includes("update") || raw.includes("edit")) return "Update";
  if (raw.includes("add") || raw.includes("create")) return "Create";
  return "Other";
};

const AuditTrendChart = ({
  data,
}: {
  data: Array<{ label: string; count: number }>;
}) => {
  const width = 620;
  const height = 190;
  const padX = 34;
  const padTop = 20;
  const padBottom = 42;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;
  const max = Math.max(1, ...data.map((d) => d.count));

  const points = data.map((d, index) => {
    const x =
      padX + (data.length <= 1 ? innerW / 2 : (index / (data.length - 1)) * innerW);
    const y = padTop + innerH - (d.count / max) * innerH;
    return { ...d, x, y };
  });

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const area = points.length
    ? `${padX},${padTop + innerH} ${polyline} ${padX + innerW},${padTop + innerH}`
    : "";

  return (
    <div className="trend-chart-shell">
      <svg viewBox={`0 0 ${width} ${height}`} className="trend-svg">
        {[0, 1, 2, 3].map((i) => {
          const y = padTop + (innerH / 3) * i;
          return (
            <line
              key={i}
              x1={padX}
              x2={padX + innerW}
              y1={y}
              y2={y}
              stroke="#edf1f5"
              strokeWidth="1"
            />
          );
        })}

        {area && <polygon points={area} fill="rgba(37, 99, 235, 0.08)" />}
        {polyline && (
          <polyline
            points={polyline}
            fill="none"
            stroke="#2563eb"
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {points.map((p, index) => (
          <g key={index}>
            <circle cx={p.x} cy={p.y} r="5" fill="#fff" stroke="#2563eb" strokeWidth="3" />
            <text
              x={p.x}
              y={Math.max(12, p.y - 12)}
              textAnchor="middle"
              className="trend-count"
            >
              {p.count}
            </text>
            <text
              x={p.x}
              y={height - 12}
              textAnchor="middle"
              className="trend-label"
            >
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const Dashboard = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [entityStats, setEntityStats] = useState<Record<string, EntityStat>>({});
  const [selectedEntityKey, setSelectedEntityKey] = useState<string | null>(null);
  const [featureKeys, setFeatureKeys] = useState<Set<string>>(new Set());
  const [featuresStatus, setFeaturesStatus] = useState<LoadState>("loading");

  const [auditTrend, setAuditTrend] = useState<Array<{ label: string; count: number }>>([]);
  const [auditActions, setAuditActions] = useState<AuditAction[]>([]);
  const [auditStatus, setAuditStatus] = useState<LoadState>("loading");

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canLoadAudit = ["system_admin", "super_super_admin"].includes(
    String(storedUser?.role ?? "")
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchEntities = useCallback(async () => {
    const initial: Record<string, EntityStat> = {};

    ENTITY_DEFINITIONS.forEach((def) => {
      initial[def.key] = {
        key: def.key,
        label: def.label,
        total: 0,
        active: 0,
        inactive: 0,
        route: def.route,
        status: "loading",
      };
    });

    setEntityStats(initial);

    const results = await Promise.all(
      ENTITY_DEFINITIONS.map(async (def) => {
        try {
          const [activeResult, inactiveResult] = await Promise.allSettled([
            fetchFilteredCount(def.client, def.endpoint, true),
            fetchFilteredCount(def.client, def.endpoint, false),
          ]);

          if (activeResult.status === "rejected" && inactiveResult.status === "rejected") {
            throw activeResult.reason ?? inactiveResult.reason;
          }

          const active =
            activeResult.status === "fulfilled" ? activeResult.value : 0;
          const inactive =
            inactiveResult.status === "fulfilled" ? inactiveResult.value : 0;

          return {
            key: def.key,
            label: def.label,
            total: active + inactive,
            active,
            inactive,
            route: def.route,
            status: "ok" as const,
          };
        } catch (error: any) {
          console.error(`Dashboard ${def.label} API failed:`, error);
          return {
            key: def.key,
            label: def.label,
            total: 0,
            active: 0,
            inactive: 0,
            route: def.route,
            status: "error" as const,
            error:
              error?.response?.data?.error ??
              error?.message ??
              `Failed to load ${def.label}`,
          };
        }
      })
    );

    setEntityStats(
      results.reduce<Record<string, EntityStat>>((acc, item) => {
        acc[item.key] = item;
        return acc;
      }, {})
    );
  }, []);

  const fetchFeatures = useCallback(async () => {
    setFeaturesStatus("loading");

    try {
      const response = await featuresApi.get("");
      const features = Array.isArray(response.data?.features)
        ? response.data.features
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      const enabled = new Set<string>(
        features
          .filter((f: any) => f?.is_enabled === true)
          .map((f: any) => String(f.feature_key))
      );

      setFeatureKeys(enabled);
      setFeaturesStatus("ok");
    } catch (error) {
      console.error("Dashboard features API failed:", error);
      setFeatureKeys(new Set());
      setFeaturesStatus("error");
    }
  }, []);

  const fetchAuditAnalytics = useCallback(async () => {
    if (!canLoadAudit) {
      setAuditStatus("ok");
      setAuditActions([]);
      setAuditTrend([]);
      return;
    }

    setAuditStatus("loading");

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const dates = Array.from({ length: 7 }, (_, index) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - index));
        return date;
      });

      const responses = await Promise.all(
        dates.map((date) =>
          actionsApi.get("/actions", {
            params: {
              date: getLocalDateString(date),
              timezone,
            },
          })
        )
      );

      const byDay = responses.map((response, index) => {
        const raw = response.data?.data ?? response.data ?? [];
        const actions = Array.isArray(raw) ? raw : [];

        return {
          label: dates[index].toLocaleDateString("en-US", { weekday: "short" }),
          count: actions.length,
          actions,
        };
      });

      const allActions = byDay
        .flatMap((day) => day.actions)
        .sort(
          (a: AuditAction, b: AuditAction) =>
            new Date(b.timestamp ?? 0).getTime() -
            new Date(a.timestamp ?? 0).getTime()
        );

      setAuditTrend(byDay.map(({ label, count }) => ({ label, count })));
      setAuditActions(allActions);
      setAuditStatus("ok");
    } catch (error) {
      console.error("Dashboard audit analytics API failed:", error);
      setAuditTrend([]);
      setAuditActions([]);
      setAuditStatus("error");
    }
  }, [canLoadAudit]);

  const refreshDashboard = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);

    await Promise.allSettled([
      fetchEntities(),
      fetchFeatures(),
      fetchAuditAnalytics(),
    ]);

    setLastUpdated(new Date());
    setLoading(false);
  }, [isAuthenticated, fetchEntities, fetchFeatures, fetchAuditAnalytics]);

  useEffect(() => {
    if (isAuthenticated) {
      void refreshDashboard();
    }
  }, [isAuthenticated, refreshDashboard]);

  const entityList = useMemo(
    () =>
      ENTITY_DEFINITIONS.map((def) => entityStats[def.key]).filter(
        (item): item is EntityStat => Boolean(item)
      ),
    [entityStats]
  );

  const selectedEntity = useMemo(
    () => entityList.find((item) => item.key === selectedEntityKey) ?? null,
    [entityList, selectedEntityKey]
  );

  const totalEntities = entityList.reduce((sum, item) => sum + item.total, 0);
  const moduleCoverage = useMemo(() => {
    return MODULE_GROUPS.map((group) => {
      const enabled =
        featuresStatus === "ok"
          ? group.pages.filter(
              (page) => !page.feature || featureKeys.has(page.feature)
            ).length
          : group.pages.filter((page) => !page.feature).length;

      return {
        ...group,
        total: group.pages.length,
        enabled,
        disabled: group.pages.length - enabled,
        percentage:
          group.pages.length > 0
            ? Math.round((enabled / group.pages.length) * 100)
            : 0,
      };
    });
  }, [featureKeys, featuresStatus]);

  const totalPages = moduleCoverage.reduce((sum, group) => sum + group.total, 0);
  const enabledPages =
    featuresStatus === "ok"
      ? moduleCoverage.reduce((sum, group) => sum + group.enabled, 0)
      : 0;

  const auditTypeBreakdown = useMemo(() => {
    const base: Record<string, number> = {
      Create: 0,
      Update: 0,
      Delete: 0,
      Restore: 0,
      Other: 0,
    };

    auditActions.forEach((action) => {
      base[actionTypeBucket(action.type, action.name)] += 1;
    });

    return base;
  }, [auditActions]);


  const entityOnline = entityList.filter((item) => item.status === "ok").length;
  const optionalSources = canLoadAudit ? 2 : 1; // features + optional audit
  const extraOnline =
    (featuresStatus === "ok" ? 1 : 0) +
    (canLoadAudit && auditStatus === "ok" ? 1 : 0);
  const sourceTotal = ENTITY_DEFINITIONS.length + optionalSources;
  const sourceOnline = entityOnline + extraOnline;

  if (loading && entityList.length === 0) {
    return (
      <div className="leen-dashboard-shell">
        <div className="dashboard-loading">
          <div className="dashboard-spinner" />
          <strong>Building live dashboard...</strong>
          <span>Reading configuration, feature and audit APIs</span>
        </div>

        <style>{dashboardStyles}</style>
      </div>
    );
  }

  return (
    <div className="leen-dashboard-shell">
      <div className="dashboard-topbar">
        <div className="brand-block">
          <div className="brand-icon">
            <Sparkles size={23} />
          </div>
          <div>
            <div className="eyebrow">LEEN Intelligence Console</div>
            <h1>Healthcare Operations Dashboard</h1>
            <p>Live configuration, readiness and governance analytics</p>
          </div>
        </div>

        <div className="topbar-actions">
         

          <div className="date-chip">
            <CalendarDays size={16} />
            <span>
              {currentDate.toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>

          <button
            type="button"
            className="refresh-button"
            onClick={() => void refreshDashboard()}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="refresh-meta">
        <Clock3 size={13} />
        {lastUpdated
          ? `Last updated ${lastUpdated.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}`
          : "Waiting for first refresh"}
      </div>

      <section className="hero-grid">
        <div className="hero-card dark">
          <div className="hero-card-icon">
            <Database size={20} />
          </div>
          <span className="hero-label">Total Configure Records</span>
          <strong>{totalEntities.toLocaleString()}</strong>
          <small>Combined records across the live configure sources</small>
        </div>

        <div className="hero-card">
          <div className="hero-card-icon green">
            <Layers3 size={20} />
          </div>
          <span className="hero-label">Configuration Modules</span>
          <strong>{MODULE_GROUPS.length}</strong>
          <small>ICD, Activity, Benefit, Validation and Configure</small>
        </div>

        <div className="hero-card">
          <div className="hero-card-icon blue">
            <Settings2 size={20} />
          </div>
          <span className="hero-label">Enabled Application Pages</span>
          <strong>
            {featuresStatus === "ok" ? `${enabledPages}/${totalPages}` : "—"}
          </strong>
          <small>
            {featuresStatus === "ok"
              ? "Calculated from Feature Control and always-on pages"
              : "Feature API unavailable"}
          </small>
        </div>

        <div className="hero-card">
          <div className="hero-card-icon amber">
            <Activity size={20} />
          </div>
          <span className="hero-label">7-Day Configuration Changes</span>
          <strong>{canLoadAudit && auditStatus === "ok" ? auditActions.length : "—"}</strong>
          <small>
            {!canLoadAudit
              ? "Available to administrator roles"
              : auditStatus === "error"
              ? "Audit API unavailable"
              : "Create, update, delete and restore events"}
          </small>
        </div>

        <div className="hero-card">
          <div className="hero-card-icon red">
            <CheckCircle2 size={20} />
          </div>
          <span className="hero-label">Live API Sources</span>
          <strong>{`${sourceOnline}/${sourceTotal}`}</strong>
          <small>Configure, feature and audit sources currently responding</small>
        </div>
      </section>

      <section className="main-dashboard-grid">
        <article className="analytics-card entity-activity-card entity-switcher-card">
          <div className="card-heading entity-switcher-heading">
            <div>
              <span className="section-kicker">Configure Health</span>
              <h2>Entity Status Overview</h2>
              <p>Select an entity to view its live status details.</p>
            </div>
          </div>

          <div className="entity-tile-grid">
            {entityList.map((item) => {
              const visual = ENTITY_VISUALS[item.key];
              const Icon = visual.icon;
              const isSelected = selectedEntityKey === item.key;

              return (
                <button
                  key={item.key}
                  type="button"
                  className={`entity-tile ${isSelected ? "selected" : ""} ${item.status}`}
                  onClick={() =>
                    setSelectedEntityKey((current) =>
                      current === item.key ? null : item.key
                    )
                  }
                  aria-pressed={isSelected}
                >
                  <span
                    className="entity-tile-icon"
                    style={{ background: visual.soft, color: visual.accent }}
                  >
                    <Icon size={20} />
                  </span>

                  <strong>{item.label}</strong>

                  <span className="entity-tile-count">
                    {item.status === "error" ? "—" : item.total.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>

          {selectedEntity && (() => {
            const visual = ENTITY_VISUALS[selectedEntity.key];
            const Icon = visual.icon;
            const activePct =
              selectedEntity.total > 0
                ? Math.round((selectedEntity.active / selectedEntity.total) * 100)
                : 0;

            return (
              <div className={`entity-reveal-panel ${selectedEntity.status}`}>
                <div className="entity-reveal-header">
                  <div className="entity-reveal-title">
                    <span
                      className="entity-reveal-icon"
                      style={{ background: visual.soft, color: visual.accent }}
                    >
                      <Icon size={22} />
                    </span>

                    <div>
                      <span className="entity-reveal-kicker">Selected entity</span>
                      <h3>{selectedEntity.label}</h3>
                      <p>
                        {selectedEntity.status === "error"
                          ? selectedEntity.error ?? "Could Not Load Data"
                          : "Live Status From Configure API"}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="entity-hide-button"
                    onClick={() => setSelectedEntityKey(null)}
                  >
                    <XCircle size={16} />
                    Hide
                  </button>
                </div>

                <div className="entity-reveal-content">
                  <div className="entity-reveal-stat primary">
                    <span>Total Records</span>
                    <strong>
                      {selectedEntity.status === "error"
                        ? "—"
                        : selectedEntity.total.toLocaleString()}
                    </strong>
                  </div>

                  <div className="entity-reveal-stat active">
                    <span>Active</span>
                    <strong>
                      {selectedEntity.status === "error"
                        ? "—"
                        : selectedEntity.active.toLocaleString()}
                    </strong>
                  </div>

                  <div className="entity-reveal-stat inactive">
                    <span>Inactive</span>
                    <strong>
                      {selectedEntity.status === "error"
                        ? "—"
                        : selectedEntity.inactive.toLocaleString()}
                    </strong>
                  </div>

                  <div className="entity-reveal-stat rate">
                    <span>Active Rate</span>
                    <strong>
                      {selectedEntity.status === "error" ? "—" : `${activePct}%`}
                    </strong>
                  </div>
                </div>

                <div className="entity-reveal-progress">
                  <div className="entity-reveal-progress-head">
                    <span>Active vs inactive</span>
                    <strong>
                      {selectedEntity.status === "error" ? "—" : `${activePct}% Active`}
                    </strong>
                  </div>

                  <div className="entity-reveal-track">
                    <span
                      style={{
                        width:
                          selectedEntity.status === "error"
                            ? "0%"
                            : `${Math.max(0, Math.min(100, activePct))}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="entity-reveal-footer">
                  <span>
                    {selectedEntity.status === "error"
                      ? "API Unavailable"
                      : `${selectedEntity.total.toLocaleString()} Total Records`}
                  </span>

                  <button
                    type="button"
                    onClick={() => navigate(selectedEntity.route)}
                    disabled={selectedEntity.status === "error"}
                  >
                    Open Details →
                  </button>
                </div>
              </div>
            );
          })()}
        </article>
      </section>

      <section className="analytics-card module-coverage-card">
        <div className="card-heading">
          <div>
            <span className="section-kicker">Application Coverage</span>
            <h2>Configuration Module Readiness</h2>
            <p>
              Covers ICD, Activity, Benefit, Validation and Configure pages from the
              navigation structure.
            </p>
          </div>
          <div className={`api-pill ${featuresStatus}`}>
            <span />
            {featuresStatus === "ok"
              ? "Feature API Live"
              : featuresStatus === "loading"
              ? "Loading"
              : "Feature API Error"}
          </div>
        </div>

        <div className="module-grid">
          {moduleCoverage.map((module, index) => (
            <div className="module-card" key={module.label}>
              <div className="module-top">
                <span className={`module-index tone-${index + 1}`}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <strong>{module.label}</strong>
                  <small>{module.total} pages</small>
                </div>
                <b>{featuresStatus === "ok" ? `${module.percentage}%` : "—"}</b>
              </div>

              <div className="module-progress">
                <span
                  style={{
                    width:
                      featuresStatus === "ok" ? `${module.percentage}%` : "0%",
                  }}
                />
              </div>

              <div className="module-footer">
                <span>
                  <CheckCircle2 size={13} />
                  {featuresStatus === "ok" ? module.enabled : "—"} enabled
                </span>
                <span>
                  <XCircle size={13} />
                  {featuresStatus === "ok" ? module.disabled : "—"} disabled
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {canLoadAudit && (
        <section className="audit-grid">
          <article className="analytics-card trend-card">
            <div className="card-heading">
              <div>
                <span className="section-kicker">Audit Intelligence</span>
                <h2>Configuration Change Trend</h2>
                <p>Live action-log volume across the last seven calendar days.</p>
              </div>
              <div className={`api-pill ${auditStatus}`}>
                <span />
                {auditStatus === "ok"
                  ? "Actions API Live"
                  : auditStatus === "loading"
                  ? "Loading"
                  : "Actions API Error"}
              </div>
            </div>

            {auditStatus === "ok" ? (
              <AuditTrendChart data={auditTrend} />
            ) : (
              <div className="chart-empty">
                <Activity size={28} />
                <strong>Audit analytics unavailable</strong>
                <span>Refresh after the Actions API is available.</span>
              </div>
            )}

            <div className="action-type-strip">
              {Object.entries(auditTypeBreakdown).map(([label, count]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      <style>{dashboardStyles}</style>
    </div>
  );
};

const dashboardStyles = `
  * { box-sizing: border-box; }

  .leen-dashboard-shell {
    min-height: 100vh;
    padding: 32px;
    padding-top: 82px;
    background:
      radial-gradient(circle at 8% 0%, rgba(37,99,235,.06), transparent 28%),
      radial-gradient(circle at 100% 14%, rgba(16,185,129,.05), transparent 24%),
      #f5f7fb;
    color: #172033;
  }

  .dashboard-loading {
    min-height: 72vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 9px;
    color: #25324a;
  }

  .dashboard-loading span {
    color: #7b879c;
    font-size: 13px;
  }

  .dashboard-spinner {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: 4px solid #dce5f0;
    border-top-color: #2563eb;
    animation: dashboard-spin .8s linear infinite;
    margin-bottom: 10px;
  }

  @keyframes dashboard-spin { to { transform: rotate(360deg); } }
  .spin { animation: dashboard-spin .8s linear infinite; }

  .dashboard-topbar {
    max-width: 1700px;
    margin: 0 auto;
    min-height: 108px;
    background: rgba(255,255,255,.92);
    border: 1px solid rgba(222,228,238,.9);
    border-radius: 24px;
    box-shadow: 0 14px 40px rgba(29,43,72,.08);
    padding: 22px 24px;
    display: flex;
    justify-content: space-between;
    gap: 28px;
    align-items: center;
    backdrop-filter: blur(14px);
  }

  .brand-block {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  .brand-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    color: white;
    background: linear-gradient(145deg, #071b35, #12386d);
    box-shadow: 0 12px 24px rgba(13,42,79,.22);
    flex-shrink: 0;
  }

  .eyebrow, .section-kicker {
    color: #718096;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .035em;
    text-transform: none;
  }

  .brand-block h1 {
    margin: 3px 0 2px;
    font-size: clamp(22px, 2vw, 31px);
    line-height: 1.1;
    letter-spacing: -.035em;
    color: #172033;
    font-weight: 900;
  }

  .brand-block p {
    margin: 0;
    font-size: 13px;
    color: #768298;
    font-weight: 500;
  }

  .topbar-actions {
    display: flex;
    align-items: center;
    gap: 11px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .source-health, .date-chip {
    height: 46px;
    padding: 0 14px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #e6ebf2;
    display: flex;
    align-items: center;
    gap: 9px;
    white-space: nowrap;
  }

  .source-health strong,
  .source-health span {
    display: block;
  }

  .source-health strong {
    font-size: 11px;
    color: #27344b;
  }

  .source-health span {
    font-size: 9px;
    color: #8b96a8;
    margin-top: 1px;
  }

  .health-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 5px rgba(16,185,129,.10);
  }

  .date-chip {
    color: #43506a;
    font-size: 11px;
    font-weight: 800;
  }

  .refresh-button {
    height: 46px;
    padding: 0 17px;
    border: none;
    border-radius: 14px;
    background: linear-gradient(145deg, #071b35, #12386d);
    color: #fff;
    font-weight: 800;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    box-shadow: 0 9px 20px rgba(13,42,79,.18);
  }

  .refresh-button:disabled {
    opacity: .6;
    cursor: wait;
  }

  .refresh-meta {
    max-width: 1700px;
    margin: 9px auto 18px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    color: #8994a7;
    padding-right: 4px;
  }

  .hero-grid {
    max-width: 1700px;
    margin: 0 auto 18px;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 14px;
    align-items: stretch;
  }

  .hero-card {
    min-height: 138px;
    padding: 19px;
    border-radius: 20px;
    background: #fff;
    border: 1px solid #e7ecf3;
    box-shadow: 0 10px 26px rgba(30,45,72,.055);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    transition: transform .2s ease, box-shadow .2s ease;
  }

  .hero-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 34px rgba(30,45,72,.085);
  }

  .hero-card.dark {
    color: white;
    background: linear-gradient(145deg, #0a203f, #102f5c);
    border-color: transparent;
  }

  .hero-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 11px;
    display: grid;
    place-items: center;
    background: #edf3ff;
    color: #2563eb;
    margin-bottom: 12px;
  }

  .hero-card.dark .hero-card-icon {
    background: rgba(255,255,255,.12);
    color: #fff;
  }

  .hero-card-icon.green { background: #dcfce7; color: #059669; }
  .hero-card-icon.red { background: #fee2e2; color: #dc2626; }
  .hero-card-icon.blue { background: #dbeafe; color: #2563eb; }
  .hero-card-icon.amber { background: #fef3c7; color: #d97706; }

  .hero-label {
    font-size: 10px;
    color: #7b8798;
    font-weight: 800;
    text-transform: none;
    letter-spacing: .015em;
  }

  .hero-card.dark .hero-label,
  .hero-card.dark small {
    color: rgba(255,255,255,.65);
  }

  .hero-card > strong {
    font-size: 30px;
    margin-top: 3px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.04em;
  }

  .hero-card small {
    margin-top: 8px;
    font-size: 10px;
    color: #8d98aa;
    line-height: 1.35;
  }

  .main-dashboard-grid,
  .audit-grid {
    max-width: 1700px;
    margin: 0 auto 18px;
    display: grid;
    gap: 18px;
  }

  .main-dashboard-grid {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }

  .audit-grid {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }

  .analytics-card {
    background: #fff;
    border: 1px solid #e6ebf2;
    border-radius: 22px;
    box-shadow: 0 10px 30px rgba(28,43,70,.055);
    padding: 23px;
    overflow: hidden;
    align-self: start;
    height: fit-content;
  }

  .card-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 22px;
  }

  .card-heading.compact {
    align-items: center;
  }

  .card-heading h2 {
    margin: 3px 0 3px;
    font-size: 17px;
    font-weight: 900;
    letter-spacing: -.025em;
    color: #1d283b;
  }

  .card-heading p {
    margin: 0;
    color: #8792a4;
    font-size: 10.5px;
    line-height: 1.5;
  }

  .legend {
    display: flex;
    gap: 14px;
    color: #7d889b;
    font-size: 10px;
    font-weight: 700;
    padding-top: 5px;
  }

  .legend > span {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .legend-dot,
  .metric-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }

  .legend-dot.active,
  .metric-dot.active { background: #10b981; }
  .legend-dot.inactive,
  .metric-dot.inactive { background: #ef4444; }

  .live-data-badge {
    height: 30px;
    padding: 0 10px;
    border: 1px solid #e8edf3;
    border-radius: 999px;
    background: #f8fafc;
    color: #66758b;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 9px;
    font-weight: 800;
    white-space: nowrap;
  }

  .entity-switcher-card {
    padding: 24px;
  }

  .entity-switcher-heading {
    margin-bottom: 18px;
  }

  .entity-tile-grid {
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 10px;
  }

  .entity-tile {
    min-height: 112px;
    border: 1px solid #e6ebf2;
    border-radius: 16px;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
    padding: 13px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    cursor: pointer;
    color: #26344a;
    transition:
      transform .18s ease,
      border-color .18s ease,
      box-shadow .18s ease,
      background .18s ease;
    box-shadow: 0 4px 14px rgba(24,39,70,.03);
  }

  .entity-tile:hover {
    transform: translateY(-2px);
    border-color: #cbd6e4;
    box-shadow: 0 12px 25px rgba(24,39,70,.07);
  }

  .entity-tile.selected {
    border-color: #2563eb;
    background:
      radial-gradient(circle at 50% 0%, rgba(37,99,235,.09), transparent 42%),
      linear-gradient(180deg, #ffffff, #f7faff);
    box-shadow:
      0 0 0 3px rgba(37,99,235,.08),
      0 14px 30px rgba(37,99,235,.10);
  }

  .entity-tile.error {
    border-color: #fecaca;
  }

  .entity-tile-icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    display: grid;
    place-items: center;
  }

  .entity-tile strong {
    font-size: 10px;
    line-height: 1.25;
    font-weight: 900;
    color: #26344a;
  }

  .entity-tile-count {
    min-width: 28px;
    height: 21px;
    padding: 0 7px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    color: #64748b;
    font-size: 8px;
    font-weight: 850;
  }

  .entity-tile.selected .entity-tile-count {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .entity-reveal-panel {
    margin-top: 16px;
    border: 1px solid #dfe6ef;
    border-radius: 20px;
    background:
      radial-gradient(circle at 95% 0%, rgba(37,99,235,.045), transparent 34%),
      #ffffff;
    padding: 18px;
    box-shadow:
      0 14px 34px rgba(28,43,70,.055),
      inset 0 1px 0 rgba(255,255,255,.75);
    animation: entity-reveal-in .18s ease-out;
  }

  .entity-reveal-panel.error {
    border-color: #fecaca;
    background: #fffafa;
  }

  @keyframes entity-reveal-in {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .entity-reveal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }

  .entity-reveal-title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .entity-reveal-icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }

  .entity-reveal-kicker {
    display: block;
    color: #94a3b8;
    font-size: 8px;
    font-weight: 800;
  }

  .entity-reveal-title h3 {
    margin: 2px 0 2px;
    color: #1e293b;
    font-size: 16px;
    line-height: 1.1;
    font-weight: 900;
  }

  .entity-reveal-title p {
    margin: 0;
    color: #8a96a8;
    font-size: 9px;
    line-height: 1.4;
  }

  .entity-hide-button {
    height: 34px;
    padding: 0 11px;
    border-radius: 10px;
    border: 1px solid #e5eaf1;
    background: #f8fafc;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 9px;
    font-weight: 850;
    cursor: pointer;
  }

  .entity-hide-button:hover {
    background: #f1f5f9;
    color: #334155;
  }

  .entity-reveal-content {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 17px;
  }

  .entity-reveal-stat {
    min-height: 78px;
    border-radius: 14px;
    border: 1px solid #e7ecf3;
    background: #f9fbfd;
    padding: 13px;
  }

  .entity-reveal-stat span,
  .entity-reveal-stat strong {
    display: block;
  }

  .entity-reveal-stat span {
    color: #8b96a8;
    font-size: 8px;
    font-weight: 750;
  }

  .entity-reveal-stat strong {
    margin-top: 8px;
    color: #253249;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -.035em;
  }

  .entity-reveal-stat.active {
    background: #effcf6;
    border-color: #d8f5e7;
  }

  .entity-reveal-stat.active strong {
    color: #047857;
  }

  .entity-reveal-stat.inactive {
    background: #fff5f6;
    border-color: #ffe3e6;
  }

  .entity-reveal-stat.inactive strong {
    color: #be123c;
  }

  .entity-reveal-stat.rate {
    background: #f3f7ff;
    border-color: #e0e9ff;
  }

  .entity-reveal-stat.rate strong {
    color: #1d4ed8;
  }

  .entity-reveal-progress {
    margin-top: 14px;
    padding: 13px 14px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #edf1f5;
  }

  .entity-reveal-progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .entity-reveal-progress-head span {
    color: #7f8b9f;
    font-size: 8px;
    font-weight: 750;
  }

  .entity-reveal-progress-head strong {
    color: #334155;
    font-size: 9px;
    font-weight: 900;
  }

  .entity-reveal-track {
    height: 8px;
    border-radius: 999px;
    background: #e7edf3;
    overflow: hidden;
  }

  .entity-reveal-track > span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #10b981, #34d399);
  }

  .entity-reveal-footer {
    margin-top: 14px;
    padding-top: 13px;
    border-top: 1px solid #edf1f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .entity-reveal-footer > span {
    color: #8c98aa;
    font-size: 8.5px;
    font-weight: 700;
  }

  .entity-reveal-footer button {
    height: 34px;
    padding: 0 12px;
    border-radius: 10px;
    border: 0;
    background: #0f2f5f;
    color: white;
    font-size: 8.5px;
    font-weight: 850;
    cursor: pointer;
  }

  .entity-reveal-footer button:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .module-coverage-card {
    max-width: 1700px;
    margin: 0 auto 18px;
  }

  .api-pill {
    height: 29px;
    padding: 0 10px;
    border-radius: 99px;
    background: #f8fafc;
    border: 1px solid #e8edf3;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 9px;
    font-weight: 800;
    color: #6e7a8e;
    white-space: nowrap;
  }

  .api-pill > span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #94a3b8;
  }

  .api-pill.ok > span { background: #10b981; }
  .api-pill.error > span { background: #ef4444; }
  .api-pill.loading > span { background: #f59e0b; }

  .module-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
  }

  .module-card {
    border: 1px solid #e8edf4;
    border-radius: 16px;
    padding: 14px;
    background: linear-gradient(180deg, #fff, #fbfcfe);
  }

  .module-top {
    display: grid;
    grid-template-columns: 32px 1fr auto;
    align-items: center;
    gap: 9px;
  }

  .module-index {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    font-size: 9px;
    font-weight: 900;
    background: #eaf1ff;
    color: #2563eb;
  }

  .tone-2 { background: #ecfdf5; color: #059669; }
  .tone-3 { background: #fff7ed; color: #ea580c; }
  .tone-4 { background: #f5f3ff; color: #7c3aed; }
  .tone-5 { background: #fdf2f8; color: #db2777; }

  .module-top strong,
  .module-top small {
    display: block;
  }

  .module-top strong {
    font-size: 10px;
    color: #2f3b50;
    font-weight: 850;
  }

  .module-top small {
    font-size: 8.5px;
    color: #96a0b0;
    margin-top: 2px;
  }

  .module-top > b {
    font-size: 13px;
    color: #26344a;
  }

  .module-progress {
    height: 6px;
    border-radius: 99px;
    background: #edf1f6;
    overflow: hidden;
    margin: 13px 0 9px;
  }

  .module-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #2563eb, #60a5fa);
  }

  .module-footer {
    display: flex;
    justify-content: space-between;
    gap: 5px;
    font-size: 8.5px;
    color: #8b96a7;
  }

  .module-footer span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .module-footer span:first-child svg { color: #10b981; }
  .module-footer span:last-child svg { color: #ef4444; }

  .trend-chart-shell {
    width: 100%;
    overflow-x: auto;
  }

  .trend-svg {
    width: 100%;
    min-width: 560px;
    height: 215px;
    overflow: visible;
  }

  .trend-label {
    fill: #8b96a8;
    font-size: 10px;
    font-weight: 700;
  }

  .trend-count {
    fill: #334155;
    font-size: 9px;
    font-weight: 900;
  }

  .action-type-strip {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 7px;
    margin-top: 4px;
  }

  .action-type-strip div {
    background: #f8fafc;
    border: 1px solid #edf1f5;
    border-radius: 10px;
    padding: 8px 9px;
  }

  .action-type-strip span,
  .action-type-strip strong {
    display: block;
  }

  .action-type-strip span {
    font-size: 8px;
    color: #8d98a9;
    text-transform: none;
    font-weight: 800;
  }

  .action-type-strip strong {
    margin-top: 2px;
    font-size: 15px;
    color: #2b374c;
  }

  .chart-empty {
    min-height: 210px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: center;
    justify-content: center;
    color: #9aa5b6;
    text-align: center;
  }

  .chart-empty.small {
    min-height: 175px;
  }

  .chart-empty strong {
    color: #536076;
    font-size: 11px;
  }

  .chart-empty span {
    font-size: 9px;
  }

  @media (max-width: 1500px) {
    .entity-tile-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  @media (max-width: 1450px) {
    .hero-grid { grid-template-columns: repeat(3, 1fr); }
    .module-grid { grid-template-columns: repeat(3, 1fr); }
  }

  @media (max-width: 1100px) {
    .dashboard-topbar {
      align-items: flex-start;
      flex-direction: column;
    }

    .topbar-actions {
      width: 100%;
      justify-content: flex-start;
    }

    .main-dashboard-grid,
    .audit-grid {
      grid-template-columns: 1fr;
    }

    .hero-grid { grid-template-columns: repeat(2, 1fr); }
    .module-grid { grid-template-columns: repeat(2, 1fr); }
    .entity-tile-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .entity-reveal-content { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @media (max-width: 720px) {
    .leen-dashboard-shell {
      padding: 18px;
      padding-top: 70px;
    }

    .hero-grid,
    .module-grid {
      grid-template-columns: 1fr;
    }

    .entity-tile-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .entity-reveal-content {
      grid-template-columns: 1fr 1fr;
    }

    .entity-reveal-header,
    .entity-reveal-footer {
      align-items: flex-start;
      flex-direction: column;
    }

    .entity-health-body {
      grid-template-columns: 72px 1fr;
    }

    .entity-mini-ring {
      width: 68px;
      height: 68px;
    }

    .source-health,
    .date-chip {
      width: 100%;
    }

    .recent-row {
      grid-template-columns: 32px 1fr;
    }

    .recent-user {
      grid-column: 2;
      text-align: left;
    }

    .action-type-strip {
      grid-template-columns: repeat(3, 1fr);
    }
  }
`;

export default Dashboard;
